/**
 * navigationActions - Write atoms for navigating the exploration breadcrumb trail.
 *
 * Handles drilling into child nodes, navigating back, and jumping to specific
 * breadcrumb positions. The navigation path is maintained as an array of
 * intermediate ObjectNodeData between the focused node and active node.
 *
 * The breadcrumb model:
 *   Focused Node → [NavigationPath intermediates...] → Active Node
 *
 * NavigationPathAtom stores ONLY intermediates. The focused node and active node
 * are stored in their own atoms and rendered at the start/end of the breadcrumb.
 */

import { atom, type WritableAtom } from 'jotai';

import {
	VisibleGraphAdjacencyAtom,
	VisibleNodesAtom,
	EXPLORED_NODE_ID_PREFIX,
} from '../atoms/derivedAtoms';
import { FocusedNodeAtom, ActiveNodeAtom, NavigationPathAtom } from '../atoms/focusAtoms';
import { LayoutNodesAtom } from '../atoms/layoutAtoms';
import { NodeRecencyAtom, HiddenNodesAtom } from '../atoms/recencyAtoms';
import type { ObjectNodeData } from '../nodes/ObjectNode';
import { bfsBidirectionalPath } from '../services/cypherQueryService';

import { DiscoverForNodeAtom } from './explorationActions';

// ============================================================================
// Drill into a child node
// ============================================================================

/**
 * Drill into a child node — show its relationships in the Explorer panel
 * without changing the focused node.
 *
 * The breadcrumb path is computed via BFS over the visible graph topology
 * (VisibleGraphAdjacencyAtom). This bidirectional adjacency map is built from
 * every non-hidden edge currently rendered on screen, so BFS always finds the
 * shortest path through any visible nodes — regardless of which relationship
 * pills are toggled.
 *
 * If BFS fails (target not reachable in the visible graph), falls back to
 * a direct Focused → Target breadcrumb.
 *
 * Usage: `const drill = useSetAtom(DrillIntoChildAtom); drill(childNodeData);`
 */
export const DrillIntoChildAtom: WritableAtom<
	null,
	[childNodeData: ObjectNodeData],
	Promise<void>
> & {
	init: null;
} = atom(null, async (get, set, childNodeData: ObjectNodeData) => {
	const focused = get(FocusedNodeAtom);
	if (!focused) {
		return;
	}

	// If drilling into the focused node itself, just go back to it
	if (childNodeData.id === focused.id) {
		set(NavigationPathAtom, []);
		set(ActiveNodeAtom, null);
		await set(DiscoverForNodeAtom, focused);
		return;
	}

	// Build a lookup of all visible ObjectNodeData so we can resolve
	// intermediate ARIs back to their full data objects.
	// Explored duplicates of base nodes have EXPLORED_NODE_ID_PREFIX prefixed IDs
	// in ReactFlow but BFS returns original ARIs, so we normalize the key
	// by stripping the prefix
	const visibleNodes = get(VisibleNodesAtom);
	const knownNodes = new Map<string, ObjectNodeData>();
	for (const n of visibleNodes) {
		const normalizedId = n.id.startsWith(EXPLORED_NODE_ID_PREFIX)
			? n.id.slice(EXPLORED_NODE_ID_PREFIX.length)
			: n.id;
		knownNodes.set(normalizedId, n.data);
	}
	// Ensure the target is always known
	knownNodes.set(childNodeData.id, childNodeData);

	// ── BFS over the visible graph topology ────────────────────────
	// VisibleGraphAdjacencyAtom is a bidirectional adjacency map built
	// from every visible (non-hidden) edge on screen. This means BFS
	// can find the shortest path through any rendered nodes, regardless
	// of which relationship pills are toggled.
	const adjacency = get(VisibleGraphAdjacencyAtom);

	const bfsPath = bfsBidirectionalPath(focused.id, childNodeData.id, adjacency);

	if (bfsPath.length > 0) {
		// bfsPath = [focused, ...intermediates, child]
		const intermediateAris = bfsPath.slice(1, -1);
		const newPath: ObjectNodeData[] = [];
		let valid = true;

		for (const ari of intermediateAris) {
			const nd = knownNodes.get(ari);
			if (nd) {
				newPath.push(nd);
			} else {
				valid = false;
				break;
			}
		}

		if (valid) {
			set(NavigationPathAtom, newPath);
			set(ActiveNodeAtom, childNodeData);
			await set(DiscoverForNodeAtom, childNodeData);
			return;
		}
	}

	// ── Fallback — direct path ─────────────────────────────────────
	// BFS couldn't find a path (node not reachable in the visible
	// graph). Show a direct Focused → Target breadcrumb.
	set(NavigationPathAtom, []);
	set(ActiveNodeAtom, childNodeData);
	await set(DiscoverForNodeAtom, childNodeData);
});

// ============================================================================
// Navigate to a specific breadcrumb node
// ============================================================================

// ============================================================================
// Go back to the focused node
// ============================================================================

/**
 * Go all the way back to the focused node — clear the entire navigation path.
 * Used when clicking the focused node breadcrumb.
 *
 * Usage: `const goBack = useSetAtom(GoBackToFocusedAtom); goBack();`
 */
export const GoBackToFocusedAtom: WritableAtom<null, [], Promise<void>> & {
	init: null;
} = atom(null, async (get, set) => {
	set(NavigationPathAtom, []);
	set(ActiveNodeAtom, null);
	const focused = get(FocusedNodeAtom);
	if (focused) {
		// Refresh recency on the restored focus + every neighbour
		// already on canvas, so the previously-hidden subtree wins
		// future overlap evaluations and re-emerges. Hidden set is
		// cleared so anything previously buried becomes visible again
		// and a fresh hide pass can run on the next expansion.
		const layoutNodes = get(LayoutNodesAtom);
		const focusedNode = layoutNodes.get(focused.id);
		if (focusedNode) {
			const now = Date.now();
			const recency = new Map(get(NodeRecencyAtom));
			recency.set(focused.id, now);
			for (const [id, node] of layoutNodes) {
				if (node.parentId === focused.id || id === focused.id) {
					recency.set(id, now);
				}
			}
			set(NodeRecencyAtom, recency);
			set(HiddenNodesAtom, new Set<string>());
		}

		await set(DiscoverForNodeAtom, focused);
	}
});
